// Push на клієнті. Два середовища:
//   1. Нативна апка (Capacitor WebView) — FCM через плагін PushNotifications,
//      доступний через window.Capacitor (міст інжектить сама оболонка).
//   2. Браузер (web) — класичний Web Push через service worker.
// ensurePushSubscription() викликати з КНОПКИ (жест юзера) — може показати промпт.
// silentResubscribe() — на старті: оновлює підписку тільки якщо дозвіл уже є.

import { api } from './api';

// ===== Нативна апка (Capacitor) =====

type CapPermState = 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied';
type CapPushPlugin = {
  checkPermissions(): Promise<{ receive: CapPermState }>;
  requestPermissions(): Promise<{ receive: CapPermState }>;
  register(): Promise<void>;
  addListener(event: string, cb: (data: unknown) => void): unknown;
};

declare global {
  interface Window {
    Capacitor?: {
      isNativePlatform?: () => boolean;
      Plugins?: { PushNotifications?: CapPushPlugin };
    };
  }
}

/** true — сторінка працює всередині нативної оболонки (Android/iOS апка). */
export function isNativeApp(): boolean {
  return !!window.Capacitor?.isNativePlatform?.();
}

function nativePlugin(): CapPushPlugin | null {
  if (!isNativeApp()) return null;
  return window.Capacitor?.Plugins?.PushNotifications ?? null;
}

/** Реєстрація в FCM: дозвіл (опційно з промптом) → register → токен → на бекенд. */
async function nativeSubscribe(allowPrompt: boolean): Promise<PushResult> {
  const pn = nativePlugin();
  if (!pn) return { ok: false, reason: 'Плагін сповіщень недоступний' };
  try {
    let perm = await pn.checkPermissions();
    if (perm.receive !== 'granted') {
      if (!allowPrompt) return { ok: false, reason: 'Дозвіл ще не надано' };
      perm = await pn.requestPermissions();
    }
    if (perm.receive !== 'granted') {
      return { ok: false, reason: 'Дозвіл на сповіщення не надано' };
    }

    const token = await new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('FCM не відповів (таймаут 15с)')), 15000);
      pn.addListener('registration', (t) => {
        clearTimeout(timer);
        resolve((t as { value: string }).value);
      });
      pn.addListener('registrationError', (e) => {
        clearTimeout(timer);
        reject(new Error('Помилка реєстрації FCM: ' + JSON.stringify(e)));
      });
      void pn.register();
    });

    await api.push.deviceToken(token);
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: (e as Error).message };
  }
}

/** Тап по сповіщенню в нативній апці → перехід на url з payload. */
export function initNativePushTapListener(): void {
  const pn = nativePlugin();
  if (!pn) return;
  pn.addListener('pushNotificationActionPerformed', (action) => {
    const url = (action as { notification?: { data?: { url?: string } } })?.notification?.data?.url;
    if (url) window.location.href = url;
  });
}

// ===== Браузер (Web Push) =====

function urlB64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const buffer = new ArrayBuffer(raw.length);
  const output = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

let swRegistered = false;

/** Реєструє service worker (ідемпотентно). Викликати один раз на старті.
 *  Потрібен і в браузері (Web Push), і в нативній апці (офлайн-кеш оболонки).
 *  У нативі пуші йдуть через FCM — SW-пуш-хендлер там просто спить, FCM не чіпаємо. */
export async function registerServiceWorker(): Promise<void> {
  if (swRegistered || !('serviceWorker' in navigator)) return;
  swRegistered = true;
  try {
    await navigator.serviceWorker.register('/sw.js');
  } catch {
    /* ignore — без SW не буде ні пушів, ні офлайн-кешу */
  }
}

export type PushResult = { ok: boolean; reason?: string };

/** Чи є сенс пропонувати юзеру ввімкнути сповіщення (підтримується + ще не вирішив). */
export async function shouldOfferPush(): Promise<boolean> {
  const pn = nativePlugin();
  if (pn) {
    try {
      const perm = await pn.checkPermissions();
      return perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale';
    } catch {
      return false;
    }
  }
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return false;
  }
  return Notification.permission === 'default';
}

/**
 * Тихо оновлює підписку, ЯКЩО дозвіл уже надано раніше. Ніколи не показує промпт —
 * викликається на кожному старті, щоб підписка/токен не «протухали».
 */
export async function silentResubscribe(): Promise<void> {
  if (isNativeApp()) {
    await nativeSubscribe(false).catch(() => {});
    return;
  }
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return;
  }
  if (Notification.permission !== 'granted') return;
  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const { publicKey } = await api.push.publicKey();
      if (!publicKey) return;
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(publicKey),
      });
    }
    const json = sub.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return;
    await api.push.subscribe({
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    });
  } catch {
    /* push не критичний */
  }
}

/**
 * Просить дозвіл на сповіщення й підписує пристрій на push (FCM у нативній апці,
 * Web Push у браузері). Повертає результат із причиною — щоб UI міг показати
 * чому не вдалось. Викликати з натискання кнопки (жест користувача).
 */
export async function ensurePushSubscription(): Promise<PushResult> {
  if (isNativeApp()) return nativeSubscribe(true);
  if (!('serviceWorker' in navigator)) return { ok: false, reason: 'Пристрій без serviceWorker' };
  if (!('PushManager' in window)) return { ok: false, reason: 'Пристрій без PushManager' };
  if (!('Notification' in window)) return { ok: false, reason: 'Пристрій без Notification API' };

  try {
    if (Notification.permission === 'denied') {
      return { ok: false, reason: 'Сповіщення заблоковано в налаштуваннях застосунку/браузера' };
    }
    if (Notification.permission === 'default') {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') return { ok: false, reason: 'Дозвіл на сповіщення не надано' };
    }

    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();

    if (!sub) {
      const { publicKey } = await api.push.publicKey();
      if (!publicKey) return { ok: false, reason: 'Сервер не віддав VAPID-ключ' };
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(publicKey),
      });
    }

    const json = sub.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
      return { ok: false, reason: 'Підписка без ключів' };
    }

    await api.push.subscribe({
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: (e as Error)?.message || String(e) };
  }
}
